import { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Modal, ModalDialog, Stack, Typography } from '@mui/joy';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import { LayoutContext } from '../../context';
import { APIGetChatSchedule } from '../../api';
import { getAccessToken } from '../../auth';
import { scheduleSummary } from './scheduleSummary';

function dateKey(value, zone) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(value));
  const values = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${values.year}-${values.month}-${values.day}`;
}
function label(value, zone) {
  return new Intl.DateTimeFormat('ru-RU', { timeZone: zone, day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

export default function ChatSchedule({ chatId, presenceById = {} }) {
  const { token, isPatient } = useContext(LayoutContext);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const titleId = useId();
  const retryRef = useRef(null);
  useEffect(() => {
    let disposed = false;
    let busy = false;
    setData(null); setError(''); setOpen(false); setSelected('');
    const load = async () => {
      if (!chatId || busy || document.visibilityState !== 'visible') return;
      busy = true;
      try {
        const next = await APIGetChatSchedule(chatId, getAccessToken() || token);
        if (!disposed) { setData(next); setError(''); }
      } catch {
        if (!disposed) { setError('Не удалось обновить расписание'); setData(null); }
      } finally { busy = false; }
    };
    retryRef.current = load;
    load();
    const interval = setInterval(load, 60000);
    document.addEventListener('visibilitychange', load);
    return () => { disposed = true; retryRef.current = null; clearInterval(interval); document.removeEventListener('visibilitychange', load); };
  }, [chatId, token]);
  const zone = data?.timezone || 'Europe/Moscow';
  const calendars = data?.calendars || [];
  const allSlots = useMemo(() => calendars.flatMap((calendar) => calendar.slots.map((slot) => ({ ...slot, name: calendar.name, calendarId: calendar.id }))), [data]);
  const dayKeys = useMemo(() => {
    const first = data?.server_now || new Date().toISOString();
    return Array.from({ length: data?.horizon_days || 35 }, (_, index) => dateKey(new Date(new Date(first).getTime() + index * 86400000), zone));
  }, [data, zone]);
  const selectedDay = selected || dayKeys[0];
  const leadingDays = (new Date(`${dayKeys[0]}T12:00:00Z`).getUTCDay() + 6) % 7;
  // Привязка интервалов к каждой затронутой дате учитывает ночные смены.
  const daySlots = (day) => allSlots.filter((slot) => dateKey(slot.start, zone) <= day && dateKey(new Date(new Date(slot.end).getTime() - 1), zone) >= day);
  const unknown = !calendars.length || calendars.some((calendar) => calendar.status === 'unknown');
  // Подсказка теперь рассчитывается отдельно по каждому врачу.
  const today = dayKeys[0];
  const hints = calendars.map((calendar) => {
    const offline = calendar.profile_id != null && presenceById[calendar.profile_id] === false;
    const network = offline ? ' Сейчас врач не в сети.' : '';
    if (calendar.status === 'unknown') return `${calendar.name}: расписание пока недоступно.${network} Сообщение можно отправить.`;
    const todaySlots = calendar.slots.filter((slot) => dateKey(slot.start, zone) <= today
      && dateKey(new Date(new Date(slot.end).getTime() - 1), zone) >= today);
    const todayText = todaySlots.length
      ? `Сегодня: ${todaySlots.map((slot) => `${label(slot.start, zone)} — ${label(slot.end, zone)}`).join('; ')}.`
      : 'Сегодня смены не запланированы.';
    const current = calendar.status === 'on_shift' ? 'Сейчас рабочая смена.' : 'Сейчас вне рабочей смены.';
    const next = calendar.status !== 'on_shift' && calendar.next_start
      ? ` Ближайшая смена: ${label(calendar.next_start, zone)}.` : '';
    return `${calendar.name}: ${current}${network} ${todayText}${next} Можно написать сейчас; врач ответит по возможности.`;
  });
  const hint = error ? 'Не удалось обновить расписание. Сообщение можно отправить.'
    : hints.length ? hints.join('\n') : 'Расписание пока не подключено. Сообщение можно отправить.';
  const summary = !data && !error
    ? { text: 'Загружаем график…', color: 'neutral' }
    : scheduleSummary(calendars, error, presenceById, (value) => label(value, zone));
  return (
    <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 0.75, flexShrink: 0,
      bgcolor: 'background.surface', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        <CalendarMonthRounded sx={{ fontSize: 18, color: 'primary.500', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography level="body-xs" title={summary.text} noWrap
            textColor={summary.color === 'success' ? 'success.700' : 'text.secondary'}>
            {summary.text}
          </Typography>
        </Box>
        <Button size="sm" variant="soft" color="primary"
          sx={{ borderRadius: '10px', flexShrink: 0, minHeight: 30 }}
          onClick={() => { setOpen(true); retryRef.current?.(); }}>Расписание</Button>
      </Stack>
      <Modal open={open} onClose={() => setOpen(false)} sx={{ zIndex: 10030 }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(20, 22, 35, 0.32)' } } }}>
        <ModalDialog aria-labelledby={titleId}
          sx={{ width: 'min(700px, calc(100vw - 24px))', maxWidth: 'none',
            maxHeight: 'calc(100dvh - 32px)', p: 0, gap: 0, overflow: 'hidden',
            borderRadius: '22px', bgcolor: 'background.surface',
            boxShadow: '0 24px 80px rgba(25, 28, 45, 0.24)' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}
            sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography id={titleId} level="title-lg">Расписание врача</Typography>
              <Typography level="body-xs" textColor="text.tertiary">Московское время</Typography>
            </Box>
            <Button variant="plain" color="neutral" size="sm" onClick={() => setOpen(false)}
              aria-label="Закрыть расписание" sx={{ flexShrink: 0 }}>Закрыть</Button>
          </Stack>
          <Box sx={{ p: { xs: 1.5, sm: 2 }, overflowY: 'auto', minHeight: 0 }}>
            {!data && !error && <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CircularProgress size="sm" /><Typography level="body-sm">Загружаем график…</Typography>
            </Stack>}
            <Box sx={{ mb: 2, px: 1.5, py: 1, borderRadius: '12px', bgcolor: 'background.level1' }}>
              <Typography level="body-sm" sx={{ whiteSpace: 'pre-line' }}>{hint}</Typography>
              {error && <Button size="sm" variant="plain" onClick={() => retryRef.current?.()}>Повторить загрузку</Button>}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5 }}>
              {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((day) => (
                <Typography key={day} level="body-xs" sx={{ textAlign: 'center', py: 0.5 }}>{day}</Typography>
              ))}
              {Array.from({ length: leadingDays }, (_, index) => <Box key={`empty-${index}`} />)}
              {dayKeys.map((day) => (
                <Button key={day} size="sm" aria-label={`Смены ${day.split('-').reverse().join('.')}`}
                  aria-pressed={day === selectedDay}
                  variant={day === selectedDay ? 'solid' : daySlots(day).length ? 'soft' : 'plain'}
                  color={day === selectedDay || daySlots(day).length ? 'primary' : 'neutral'}
                  onClick={() => setSelected(day)} sx={{ p: 0.5, minWidth: 0, minHeight: 36, borderRadius: '10px' }}>
                  {day.slice(8)}.{day.slice(5, 7)}
                </Button>
              ))}
            </Box>
            <Typography level="title-sm" sx={{ mt: 2, mb: 1 }}>Смены {selectedDay.split('-').reverse().join('.')}</Typography>
            <Stack spacing={1}>
              {daySlots(selectedDay).sort((a, b) => a.start.localeCompare(b.start)).map((slot) => (
                <Box key={`${slot.calendarId}-${slot.start}`} sx={{ p: 1.5, borderRadius: '14px',
                  border: '1px solid', borderColor: 'divider', bgcolor: 'background.surface' }}>
                  <Typography level="title-sm">{slot.name}</Typography>
                  <Typography level="body-sm" sx={{ mt: 0.5 }}>{label(slot.start, zone)} — {label(slot.end, zone)}</Typography>
                </Box>
              ))}
              {!daySlots(selectedDay).length && <Typography level="body-sm" textColor="text.tertiary">
                {unknown || error ? 'Нет актуальных данных о сменах' : 'На эту дату смены не запланированы'}
              </Typography>}
            </Stack>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 2 }}>
              Сообщение можно отправить вне смены. График не гарантирует мгновенный ответ и не означает свободное время для записи.
            </Typography>
            {!isPatient && <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 1 }}>
              Смены меняются в Яндекс Календаре. Сайт проверяет изменения каждый час.
            </Typography>}
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
}